import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { McpAccessTokenDTO } from '@recurrency/core-api-schema/dist/users/mcpAccessTokens';
import { Modal, Typography, notification } from 'antd';
import { ColumnType } from 'antd/lib/table';

import { Button } from 'components/Button';
import { Table } from 'components/Table';

import { captureAndShowError } from 'utils/error';
import { formatDate } from 'utils/formatting';

export interface McpSessionsTableProps {
  items: McpAccessTokenDTO[];
  isLoading: boolean;
  /** Optional user email resolver for the tenant admin view. */
  resolveUserEmail?: (userId: string) => string;
  /** Optional tenant label resolver for the account (self) view. */
  resolveTenantLabel?: (tenantId: string) => string;
  onRevoke: (token: McpAccessTokenDTO) => Promise<void>;
}

function clientDisplay(token: McpAccessTokenDTO): string {
  return token.clientLabel?.trim() || token.clientId;
}

function isExpired(token: McpAccessTokenDTO): boolean {
  return new Date(token.expiresAt).getTime() <= Date.now();
}

export function McpSessionsTable({
  items,
  isLoading,
  resolveUserEmail,
  resolveTenantLabel,
  onRevoke,
}: McpSessionsTableProps) {
  const confirmRevoke = (token: McpAccessTokenDTO) => {
    Modal.confirm({
      title: 'Revoke this MCP session?',
      icon: <ExclamationCircleOutlined />,
      content: `Clients using this session (${clientDisplay(token)}) will need to reconnect.`,
      okText: 'Revoke',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await onRevoke(token);
          notification.success({ message: 'MCP session revoked' });
        } catch (err) {
          captureAndShowError(err, 'Unable to revoke MCP session');
          throw err;
        }
      },
    });
  };

  const columns: ColumnType<McpAccessTokenDTO>[] = [
    {
      title: 'Client',
      key: 'client',
      render: (_: unknown, token: McpAccessTokenDTO) => clientDisplay(token),
    },
    ...(resolveTenantLabel
      ? [
          {
            title: 'Tenant',
            key: 'tenant',
            render: (_: unknown, token: McpAccessTokenDTO) => resolveTenantLabel(token.tenantId),
          } as ColumnType<McpAccessTokenDTO>,
        ]
      : []),
    ...(resolveUserEmail
      ? [
          {
            title: 'Email',
            key: 'userEmail',
            render: (_: unknown, token: McpAccessTokenDTO) => resolveUserEmail(token.userId),
          } as ColumnType<McpAccessTokenDTO>,
        ]
      : []),
    {
      title: 'ERP role',
      dataIndex: 'erpRole',
      key: 'erpRole',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => formatDate(value, true),
    },
    {
      title: 'Last used',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (value: string | null) => (value ? formatDate(value, true) : '—'),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (value: string) => formatDate(value, true),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: unknown, token: McpAccessTokenDTO) =>
        isExpired(token) ? (
          <Typography.Text type="secondary" disabled>
            Expired
          </Typography.Text>
        ) : (
          <Button type="link" danger onClick={() => confirmRevoke(token)}>
            Revoke
          </Button>
        ),
    },
  ];

  return (
    <Table
      rowKey="jti"
      columns={columns}
      data={items}
      isLoading={isLoading}
      pagination={false}
      locale={{ emptyText: 'No active MCP sessions' }}
    />
  );
}
